
export const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // Handle Firestore Timestamp
  if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
    return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
  }

  // Handle Date object
  if (timestamp instanceof Date) return timestamp;

  // Handle strings
  if (typeof timestamp === 'string') {
    // If timestamp already has timezone info (+00:00, Z, etc.), use it as-is
    if (timestamp.includes('+') || timestamp.includes('Z')) {
      return new Date(timestamp);
    }
    // If no timezone info, assume UTC and append 'Z'
    return new Date(timestamp + 'Z');
  }
  
  // Fallback for numbers (ms)
  if (typeof timestamp === 'number') return new Date(timestamp);

  return null;
};

export const formatTimestamp = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date) return '--:--';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

export const formatTimestampWithSeconds = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date) return '--:--:--';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

export const formatDateTime = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const isNaiveDateTimeLocal = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value);

export const getLocalDateTimeInputValue = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const parsePlannedDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' && isNaiveDateTimeLocal(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return parseTimestamp(value);
};

export const toLocalDateTimeInput = (value) => {
  const d = parsePlannedDateTime(value);
  if (!d || Number.isNaN(d.getTime())) return '';
  return getLocalDateTimeInputValue(d);
};

export const localDateTimeToISO = (localValue) => {
  if (!localValue) return '';
  if (typeof localValue === 'string' && (localValue.includes('Z') || /[+-]\d{2}:\d{2}$/.test(localValue))) {
    const date = new Date(localValue);
    return Number.isNaN(date.getTime()) ? localValue : date.toISOString();
  }
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? localValue : date.toISOString();
};

export const resolvePlannedIncidentWindows = (incident) => {
  const raw = incident?.planned_windows;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .map((window, index) => ({
        index,
        start: window?.start || incident?.planned_start_at || '',
        end: window?.end || incident?.planned_end_at || '',
      }))
      .filter((window) => window.start || window.end);
  }
  if (incident?.planned_start_at || incident?.planned_end_at) {
    return [{
      index: 0,
      start: incident.planned_start_at || '',
      end: incident.planned_end_at || '',
    }];
  }
  return [];
};

export const getIncidentWindowHelperLabel = (index, totalWindows) => {
  if (totalWindows <= 1 || index === 0) return 'Based on the First Incident Window';
  return `Based on Incident Window #${index + 1}`;
};

export const getIncidentWindowSelectLabel = (index, totalWindows) => {
  if (totalWindows <= 1) return 'Incident Window';
  if (index === 0) return 'First Incident Window';
  return `Incident Window #${index + 1}`;
};

const formatRelativeDuration = (totalMinutes) => {
  const minutes = Math.max(1, Math.round(totalMinutes));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const hourLabel = hours === 1 ? '1 hr' : `${hours} hrs`;
  if (remainder === 0) return hourLabel;
  return `${hourLabel} ${remainder} min`;
};

export const computeActionTimingContext = (scheduledAt, windowStart, windowEnd) => {
  const actionDate = parsePlannedDateTime(scheduledAt);
  const startDate = parsePlannedDateTime(windowStart);
  const endDate = parsePlannedDateTime(windowEnd);

  if (!actionDate || Number.isNaN(actionDate.getTime())) {
    return { timing: '', timing_context: '', error: 'Select a valid date and time.' };
  }
  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return { timing: '', timing_context: '', error: 'Planned incident window is not configured.' };
  }

  const actionMs = actionDate.getTime();
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();

  if (actionMs >= startMs && actionMs <= endMs) {
    return { timing: 'During Incident Window', timing_context: 'during', error: '' };
  }

  if (actionMs < startMs) {
    const diffMinutes = Math.round((startMs - actionMs) / 60000);
    return {
      timing: diffMinutes <= 0 ? 'Before Incident Window' : `Before ${formatRelativeDuration(diffMinutes)}`,
      timing_context: 'before',
      error: '',
    };
  }

  const diffMinutes = Math.round((actionMs - endMs) / 60000);
  return {
    timing: diffMinutes <= 0 ? 'After Incident Window' : `After ${formatRelativeDuration(diffMinutes)}`,
    timing_context: 'after',
    error: '',
  };
};

export const isBeforeMitigationTask = (task) => {
  if (!task) return false;
  if (task.timing_context === 'before') return true;
  const timing = String(task.timing || '').toLowerCase();
  return timing.includes('before') || timing.includes('pre') || timing.includes('readiness') || timing.startsWith('t-');
};

export const UNKNOWN_DEPARTMENT = 'Unknown Department';

export const resolveDepartmentDisplayName = (value, departments = [], fallback = UNKNOWN_DEPARTMENT) => {
  const deptList = Array.isArray(departments) ? departments : [];

  const resolveOne = (entry) => {
    if (entry == null || entry === '') return null;
    const token = String(entry).trim();
    if (!token) return null;

    const byId = deptList.find((d) => String(d.id || d._id) === token);
    if (byId) return byId.department_name || byId.name || fallback;

    const byName = deptList.find(
      (d) => String(d.department_name || d.name || '').trim().toLowerCase() === token.toLowerCase()
    );
    if (byName) return byName.department_name || byName.name || token;

    if (/^dept[_-]/i.test(token) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token)) {
      return fallback;
    }

    return token;
  };

  if (Array.isArray(value)) {
    const names = value.map(resolveOne).filter(Boolean);
    return names.length ? [...new Set(names)].join(', ') : fallback;
  }

  if (typeof value === 'string' && value.includes(',')) {
    const names = value.split(',').map((part) => resolveOne(part.trim())).filter(Boolean);
    return names.length ? [...new Set(names)].join(', ') : fallback;
  }

  return resolveOne(value) || fallback;
};

export const resolvePlanDepartmentDisplayName = (plan, departments = [], fallback = 'General') => {
  if (!plan) return fallback;

  const candidates = [plan.department_id, plan.department].filter(Boolean);
  for (const candidate of candidates) {
    const resolved = resolveDepartmentDisplayName(candidate, departments, null);
    if (resolved) return resolved;
  }

  return fallback;
};

export const resolvePlanOwnerDisplayName = (
  plan,
  { departments = [], appUsers = [], crewOptions = [], fallback = 'General' } = {}
) => {
  if (!plan) {
    return { type: 'department', typeLabel: 'Department / Unit', name: fallback };
  }

  const type = plan.owner_type || 'department';
  const typeLabels = {
    department: 'Department / Unit',
    user: 'Specific Individual',
    crew: 'Team / Crew',
  };
  const typeLabel = typeLabels[type] || type;

  if (plan.owner_name) {
    return { type, typeLabel, name: plan.owner_name };
  }

  const ownerId = plan.owner_id || plan.department_id || plan.department;

  if (type === 'user') {
    const user = (appUsers || []).find((entry) => String(entry.id) === String(ownerId));
    const name = user?.name || user?.email || user?.user_account;
    if (name) return { type, typeLabel, name };
    return { type, typeLabel, name: resolveDepartmentDisplayName(ownerId, [], fallback) };
  }

  if (type === 'crew') {
    const crew = (crewOptions || []).find((entry) => String(entry.id) === String(ownerId));
    if (crew) {
      return { type, typeLabel, name: `${crew.team_name} — ${crew.crew_name}` };
    }
    return { type, typeLabel, name: fallback };
  }

  return {
    type: 'department',
    typeLabel: typeLabels.department,
    name: resolvePlanDepartmentDisplayName(plan, departments, fallback),
  };
};

export const buildUserComboboxOptions = (appUsers = []) =>
  (appUsers || [])
    .filter((user) => user?.id)
    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, { sensitivity: 'base' }))
    .map((user) => {
      const namePart = (user.name || '').trim() || (user.user_account || '').trim() || 'Unknown';
      const emailPart = (user.email || '').trim();
      const positionPart = (user.position || '').trim();
      let label = namePart;
      if (positionPart) label += ` (${positionPart})`;
      if (emailPart) label += ` — ${emailPart}`;
      return { value: String(user.id), label };
    });

export const resolveAssigneeDisplayName = (task, appUsers = [], fallback = 'Unassigned') => {
  if (!task) return fallback;
  if (task.assignee_id) {
    const user = (appUsers || []).find((entry) => String(entry.id) === String(task.assignee_id));
    if (user) return user.name || user.email || user.user_account || fallback;
  }
  if (task.assignee && !/^[0-9a-f-]{36}$/i.test(String(task.assignee))) {
    return task.assignee;
  }
  return fallback;
};

const resolveAppUserRecord = (appUsers, uid) => {
  const id = String(uid || '').trim();
  if (!id) return null;
  return (appUsers || []).find(
    (user) =>
      String(user.id) === id ||
      String(user.email || '').toLowerCase() === id.toLowerCase() ||
      String(user.user_account || '').toLowerCase() === id.toLowerCase()
  );
};

const collectCrewUserIdsFromTeams = (crewId, appUsers = [], teams = []) => {
  const ids = new Set();
  const cid = String(crewId || '').trim();
  if (!cid) return [];

  (appUsers || []).forEach((user) => {
    if (user?.is_active === false) return;
    if (String(user.crew_id || '') === cid && user.id) {
      ids.add(String(user.id));
    }
  });

  (teams || []).forEach((team) => {
    (team.crews || []).forEach((crew) => {
      if (String(crew?.id || '') !== cid) return;
      (crew.escalation_rules || []).forEach((rule) => {
        if (rule?.is_active === false) return;
        const targets = Array.isArray(rule.escalated_to)
          ? rule.escalated_to
          : rule.escalated_to != null
            ? [rule.escalated_to]
            : [];
        targets.forEach((target) => {
          const user = resolveAppUserRecord(appUsers, target);
          if (user?.id && user.is_active !== false) {
            ids.add(String(user.id));
          }
        });
      });
    });
  });

  return [...ids];
};

/** Expand a recipient list into individual active users (de-duplicated). */
export const expandDistributionRecipients = ({
  distributionList = [],
  appUsers = [],
  departments = [],
  teams = [],
} = {}) => {
  const seen = new Set();
  const results = [];

  const pushUser = (user, source) => {
    if (!user?.id || user.is_active === false) return;
    const userId = String(user.id);
    if (seen.has(userId)) return;
    seen.add(userId);

    let teamName = '';
    let crewName = '';
    const crewId = user.crew_id ? String(user.crew_id) : '';
    if (crewId) {
      (teams || []).some((team) =>
        (team.crews || []).some((crew) => {
          if (String(crew?.id || '') !== crewId) return false;
          teamName = team.team_name || '';
          crewName = crew.crew_name || '';
          return true;
        })
      );
    }

    results.push({
      userId,
      userName: user.name || user.full_name || user.email || user.user_account || 'Unknown',
      email: user.email || '',
      departmentName:
        user.department_unit ||
        user.department ||
        source?.name ||
        '',
      teamName,
      crewName,
      offDuty: user.enable_duty_check === false,
      sourceType: source?.type,
      sourceId: source?.id,
      sourceName: source?.name,
    });
  };

  (distributionList || []).forEach((dist) => {
    const distType = String(dist?.type || '').toLowerCase();
    const distId = String(dist?.id || '');

    if (distType === 'user') {
      pushUser(resolveAppUserRecord(appUsers, distId), dist);
      return;
    }

    if (distType === 'department') {
      const deptKeys = new Set([distId]);
      (departments || []).forEach((dept) => {
        if (String(dept?.id || '') === distId && dept?.department_name) {
          deptKeys.add(String(dept.department_name));
        }
      });
      (appUsers || []).forEach((user) => {
        if (user?.is_active === false) return;
        const belongs =
          deptKeys.has(String(user.department_unit || '')) ||
          deptKeys.has(String(user.department || '')) ||
          (user.departments_units || []).some((deptRef) => deptKeys.has(String(deptRef)));
        if (belongs) pushUser(user, dist);
      });
      return;
    }

    if (distType === 'crew') {
      collectCrewUserIdsFromTeams(distId, appUsers, teams).forEach((uid) => {
        pushUser(resolveAppUserRecord(appUsers, uid), dist);
      });
    }
  });

  return results;
};

export const filterAssigneeUsers = (appUsers = [], { ownerType, ownerId } = {}) => {
  if (!ownerType || !ownerId) return appUsers;
  if (ownerType === 'department') {
    return (appUsers || []).filter(
      (user) =>
        String(user.department_unit || '') === String(ownerId) ||
        (user.departments_units || []).some((deptId) => String(deptId) === String(ownerId))
    );
  }
  if (ownerType === 'user') {
    return (appUsers || []).filter((user) => String(user.id) === String(ownerId));
  }
  return appUsers;
};

export const computeScheduledActionSlaFromMeta = (
  { notifiedAt, withinMinutes, scheduledAt } = {},
  referenceTime = new Date()
) => {
  const now = referenceTime instanceof Date ? referenceTime : new Date(referenceTime);

  if (scheduledAt) {
    const deadline = parsePlannedDateTime(scheduledAt);
    if (deadline && !Number.isNaN(deadline.getTime())) {
      return formatActionSlaState(deadline.getTime() - now.getTime(), deadline);
    }
  }

  const start = parsePlannedDateTime(notifiedAt);
  if (start && withinMinutes != null && !Number.isNaN(Number(withinMinutes))) {
    const deadline = new Date(start.getTime() + Number(withinMinutes) * 60000);
    return formatActionSlaState(deadline.getTime() - now.getTime(), deadline);
  }

  return null;
};

const formatActionSlaState = (diffMs, deadline) => {
  const isOverdue = diffMs < 0;
  const absMs = Math.abs(diffMs);
  return {
    isOverdue,
    minutesRemaining: Math.floor(absMs / 60000),
    secondsRemaining: Math.floor((absMs % 60000) / 1000),
    diffMs,
    deadline,
  };
};

export const formatElapsedTime = (milliseconds) => {
  const totalMinutes = Math.ceil(milliseconds / 60000);
  
  if (totalMinutes < 60) {
    // Under 60 minutes: "45m"
    return `${totalMinutes}m`;
  }
  
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  
  if (totalHours < 24) {
    // 60+ minutes, under 24 hours: "1h 30m"
    if (remainingMinutes === 0) {
      return `${totalHours}h`;
    }
    return `${totalHours}h ${remainingMinutes}m`;
  }
  
  // 24+ hours: "1d 5h 30m"
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  
  if (remainingHours === 0 && remainingMinutes === 0) {
    return `${days}d`;
  }
  if (remainingMinutes === 0) {
    return `${days}d ${remainingHours}h`;
  }
  return `${days}d ${remainingHours}h ${remainingMinutes}m`;
};
